import { z } from 'zod';
import type { InboundStatus } from '../enums';
import type { MAPPABLE_FIELDS } from '../funnel/mapping';
import { mappingTargetSchema, type MappingPreview } from './inbound';

export const importPreviewSchema = z.object({
  companyId: z.string().uuid(),
  /** Contenido CSV en texto (UTF-8). */
  csv: z.string().min(1).max(20_000_000),
  fieldMap: z.record(mappingTargetSchema, z.string()).optional(),
  defaults: z.record(mappingTargetSchema, z.string()).optional(),
  projectId: z.string().uuid().optional().nullable(),
  /** Asesor por defecto: se usa cuando la fila no trae columna "Asesor"; si tampoco hay, round-robin del proyecto. */
  advisorId: z.string().uuid().optional().nullable(),
  /** Sistema de origen (csv, evolta, sperant…): define la clave de `external_ids` y el preset de columnas. */
  source: z
    .string()
    .regex(/^[a-z0-9][a-z0-9-_]{1,40}$/)
    .default('csv'),
});
export type ImportPreviewInput = z.infer<typeof importPreviewSchema>;

export const importRunSchema = importPreviewSchema.extend({
  name: z.string().trim().min(2).max(120),
});
export type ImportRunInput = z.infer<typeof importRunSchema>;

/** Reproceso en bloque de las filas en ERROR de una importación ya terminada (`POST /imports/:id/retry`). */
export const importRetrySchema = z.object({
  /** Proyecto por defecto para el reproceso; null = usar solo la columna "Proyecto" de cada fila. */
  projectId: z.string().uuid().nullable().optional(),
  /** Asesor por defecto para el reproceso; null = round-robin del proyecto. */
  advisorId: z.string().uuid().nullable().optional(),
  /** Quita la columna "Asesor" del mapeo: las filas con asesor desconocido pasan al asesor por defecto / round-robin. */
  ignoreAdvisorColumn: z.boolean().default(false),
});
export type ImportRetryInput = z.infer<typeof importRetrySchema>;
export type ImportRetryPayload = z.input<typeof importRetrySchema>;

/** Una fila del archivo importado con su resultado (`GET /imports/:id/rows`). */
export interface ImportRowDto {
  /** Número de fila de datos en el archivo (1 = primera fila debajo de la cabecera). */
  row: number;
  status: InboundStatus;
  error: string | null;
  leadId: string | null;
  leadName: string | null;
  /** La fila tal como vino en el archivo (cabecera → valor). */
  data: Record<string, string>;
}

export interface ImportRowsResponse {
  importId: string;
  /** Cabeceras del archivo en el orden en que aparecen. */
  headers: string[];
  items: ImportRowDto[];
}

export interface ImportPreviewResponse {
  headers: string[];
  delimiter: string;
  totalRows: number;
  suggestedFieldMap: Partial<Record<(typeof MAPPABLE_FIELDS)[number], string>>;
  sample: Array<{ row: Record<string, string>; preview: MappingPreview }>;
}

export interface ImportDto {
  id: string;
  name: string;
  source: string;
  status: 'PENDIENTE' | 'EN_PROCESO' | 'COMPLETADO' | 'ERROR';
  totalRows: number;
  processed: number;
  created: number;
  recontacts: number;
  errors: number;
  projectId: string | null;
  projectName: string | null;
  advisorId: string | null;
  advisorName: string | null;
  /** Cabecera del archivo mapeada a "Asesor" (null = la carga no trae asesor por fila). */
  advisorColumn: string | null;
  /** Cabeceras del archivo en su orden; mapeo y valores por defecto con los que se procesa cada fila. */
  headers: string[];
  fieldMap: Record<string, string>;
  defaults: Record<string, string>;
  createdByName: string | null;
  createdAt: string;
  finishedAt: string | null;
  /** Solo en `GET /imports/:id`: motivos de error agrupados (los 10 más frecuentes). */
  errorSummary?: Array<{ error: string; count: number }>;
}
